// Authority graph — single source of truth for machine-readable
// Person + Expertise + Topic-cluster data consumed by AI assistants,
// retrieval systems, and rich-result validators.
//
// Served as JSON-LD at /authority.json and validated by
// scripts/validate-authority.mjs.

import { SITE, SOCIAL, SOLUTIONS } from "@/lib/site";
import { ASK_ARIF_CATEGORIES, ASK_ARIF_ARTICLES } from "@/lib/askArif";

const SITE_URL = SITE.url;

const SAME_AS = [SOCIAL.linkedin, SOCIAL.instagram, SOCIAL.twitter, SOCIAL.youtube];

const EXPERTISE_AREAS = [
  {
    name: "Security Infrastructure",
    description:
      "Enterprise-grade surveillance, access control, VMS and incident response architectures for mission-critical assets.",
    yearsOfExperience: 23,
    skills: ["AI Surveillance", "Video Management Systems", "Access Control", "Intrusion Detection"],
  },
  {
    name: "Perimeter Protection",
    description:
      "Multi-layer perimeter intrusion detection across kilometres of fence-line for government, industrial and critical-infrastructure sites.",
    yearsOfExperience: 20,
    skills: ["PIDS", "Fence Sensors", "Radar", "Thermal Imaging"],
  },
  {
    name: "Smart Automation & Intelligent Buildings",
    description:
      "Unified BMS/IBMS programs that orchestrate lighting, climate, security and AV for responsive, efficient buildings.",
    yearsOfExperience: 18,
    skills: ["Building Automation", "IBMS", "KNX", "Crestron", "Lutron"],
  },
  {
    name: "Luxury Home Cinema",
    description:
      "Reference-grade private theatres engineered for acoustics, calibrated to Dolby Atmos and THX targets.",
    yearsOfExperience: 15,
    skills: ["Dolby Atmos", "Acoustic Engineering", "Room Calibration", "Private Theatre Design"],
  },
  {
    name: "Auditorium & Enterprise AV",
    description:
      "Flagship audiovisual systems for auditoriums, boardrooms, training centres and control rooms at scale.",
    yearsOfExperience: 20,
    skills: ["Line Array", "Video Conferencing", "Digital Signage", "Control Rooms"],
  },
] as const;

export function buildAuthorityGraph() {
  const personId = `${SITE_URL}/#person`;
  const orgId = `${SITE_URL}/#organization`;

  const person = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": personId,
    name: "Arif Khan",
    url: SITE_URL,
    jobTitle: SITE.role,
    description: SITE.description,
    image: {
      "@type": "ImageObject",
      url: `${SITE_URL}/arif-khan.webp`,
      contentUrl: `${SITE_URL}/arif-khan.webp`,
    },
    mainEntityOfPage: SITE_URL,
    worksFor: { "@id": orgId },
    sameAs: SAME_AS,
    knowsAbout: EXPERTISE_AREAS.map((e) => e.name),
    hasOccupation: {
      "@type": "Occupation",
      name: SITE.role,
      occupationalCategory: "Security Infrastructure & Building Technology",
      experienceRequirements: "23+ years",
    },
  };

  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": orgId,
    name: SITE.organization,
    url: SITE_URL,
    email: SITE.email,
    sameAs: SAME_AS,
    founder: { "@id": personId },
    employee: { "@id": personId },
  };

  const expertise = EXPERTISE_AREAS.map((area) => ({
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name: area.name,
    description: area.description,
    inDefinedTermSet: `${SITE_URL}/authority.json#expertise`,
    additionalProperty: [
      { "@type": "PropertyValue", name: "yearsOfExperience", value: area.yearsOfExperience },
      { "@type": "PropertyValue", name: "skills", value: area.skills.join(", ") },
    ],
  }));

  const topicClusters = ASK_ARIF_CATEGORIES.map((cat) => {
    const articles = ASK_ARIF_ARTICLES.filter((a) => a.category === cat.slug);
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${SITE_URL}/ask-arif/category/${cat.slug}#cluster`,
      name: cat.title,
      description: cat.short,
      url: `${SITE_URL}/ask-arif/category/${cat.slug}`,
      about: { "@id": personId },
      author: { "@id": personId },
      hasPart: articles.map((a) => ({
        "@type": "Article",
        "@id": `${SITE_URL}/ask-arif/${a.slug}`,
        headline: a.question,
        description: a.shortAnswer,
        url: `${SITE_URL}/ask-arif/${a.slug}`,
        datePublished: a.publishedAt,
        image: `${SITE_URL}/og-arifkhan.jpg`,
        author: { "@id": personId },
        publisher: { "@id": orgId },
        keywords: a.tags.join(", "),
      })),
    };
  });

  const solutions = SOLUTIONS.map((s) => ({
    "@type": "Service",
    "@id": `${SITE_URL}/solutions/${s.slug}`,
    name: s.title,
    description: s.short,
    url: `${SITE_URL}/solutions/${s.slug}`,
    provider: { "@id": orgId },
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [
      person,
      organization,
      ...expertise,
      ...topicClusters,
      ...solutions,
    ],
    meta: {
      generatedAt: new Date().toISOString(),
      primaryEntity: personId,
      personId,
      organizationId: orgId,
      counts: {
        expertise: expertise.length,
        topicClusters: topicClusters.length,
        articles: ASK_ARIF_ARTICLES.length,
        solutions: solutions.length,
      },
    },
  };
}

export { EXPERTISE_AREAS };
